import { collection, doc, getDoc, getDocs, limit, orderBy, query, startAfter, updateDoc, where, arrayUnion } from "firebase/firestore";
import * as UsersReducer from "./reducer";
import { Collections, CollectionsClass } from "../../utils/firestore/collections";
import { AppDispatch } from "../store";
import { handleErrors } from "../../utils/firestore/handleErrors";
import { User, UserStatusTypes, UserVerifiedContactInfo } from "../../models/auth/auth.model";

export const USERS_PAGE_SIZE = 10;

class UsersAction {
    static getUsers = (
        status: UserStatusTypes,
        action: "next" | "prev",
        docsHistory: any[],
        page: number,
        dispatch: AppDispatch
    ) => {
        dispatch(UsersReducer.setIsLoading());
        const baseConstraints = [
            orderBy("created_at", "desc"),
            where("status", "==", status),
            limit(USERS_PAGE_SIZE),
        ];
        const cursor = docsHistory[page - 1];
        const q = query(
            collection(CollectionsClass.instance, Collections.USERS),
            orderBy("created_at", "desc"),
            where("status", "==", status),
            ...(page > 1 && cursor ? [startAfter(cursor)] : []),
            limit(USERS_PAGE_SIZE),
        );
        getDocs(q)
            .then(res => {
                const users: User[] = res.docs.map(d => d.data() as User);
                dispatch(UsersReducer.getUsers(users));
                if (res.docs.length > 0 && action === "next") {
                    dispatch(UsersReducer.setDocsHistory([...docsHistory, res.docs[res.docs.length - 1]]));
                } else if (action === "prev" && docsHistory.length > 1) {
                    const newHistory = docsHistory.slice(0, -1);
                    dispatch(UsersReducer.setDocsHistory(newHistory));
                }
            })
            .catch((e) => dispatch(UsersReducer.setError(handleErrors(e))));
    }

    static searchByEmail = (
        email: string,
        dispatch: AppDispatch
    ) => {
        dispatch(UsersReducer.setIsLoading());
        const q = query(
            collection(CollectionsClass.instance, Collections.USERS),
            where("email", "==", email),
            limit(25),
        );
        getDocs(q)
            .then(res => dispatch(UsersReducer.getUserByEmailSuccess(res.docs.map(doc => doc.data() as User)[0])))
            .catch((e) => dispatch(UsersReducer.setError(handleErrors(e))));
    }

    static setUserStatus = (
        userId: string,
        status: UserStatusTypes,
        dispatch: AppDispatch
    ) => {
        dispatch(UsersReducer.setIsLoading());
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${userId}`);
        updateDoc(docRef, { status })
            .then(() => dispatch(UsersReducer.updateUserStatusSuccess()))
            .catch((e) => dispatch(UsersReducer.setError(handleErrors(e))));
    }
    
    static setUserSocialMediaStatus = (
        userId: string,
        verifiedContacts: UserVerifiedContactInfo,
        dispatch: AppDispatch
    ) => {
        dispatch(UsersReducer.setIsLoading());
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${userId}`);
        updateDoc(docRef, { verifiedContacts })
            .then(() => dispatch(UsersReducer.updateVerifiedContactSuccess()))
            .catch((e) => dispatch(UsersReducer.setError(handleErrors(e))));
    }

    static clearState = (
        dispatch: AppDispatch
    ) => {
        dispatch(UsersReducer.clearState());
    }

    static addExistingPostToUser = (
        userId: string,
        postDocId: string,
        dispatch: AppDispatch
    ) => {
        dispatch(UsersReducer.setIsLoading());
        const postDocRef = doc(CollectionsClass.instance, Collections.POSTS, postDocId);
        return getDoc(postDocRef)
            .then(snap => {
                if (!snap.exists()) throw new Error("Post not found");
                const post = snap.data();
                const userDocRef = doc(CollectionsClass.instance, Collections.USERS, `user_${userId}`);
                return updateDoc(userDocRef, { posts: arrayUnion(post) });
            })
            .then(() => {
                // clear loading state
                dispatch(UsersReducer.clearState());
            })
            .catch((e) => dispatch(UsersReducer.setError(handleErrors(e))));
    }
}

export default UsersAction;